import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { asyncForLoop } from '@robocorp/robocloud-shared-utils';
import { runtimeService } from '~/services/workspaceAPI';
import { CreateWorkerGroupInput } from '~/schemas/workersSchemas';
import {
  ApiQueryFnData,
  formatErrorPayload,
  UseApiMutationOptions,
  UseApiQueryOptions,
  useCurrentWorkspaceQueryKey,
  validateApiResponse,
} from '../utils';

const ROOT_KEY = 'runtimes_groups';
const ROOT_LINK_KEY = 'runtimes_groups_link';
export const getRuntimeGroupRootQueryKey = (workspaceId: string) => [ROOT_KEY, workspaceId];
export const getRuntimeGroupQueryKey = (groupId: string) => [ROOT_KEY, groupId];
export const getRuntimeGroupKeyRootQueryKey = () => [ROOT_LINK_KEY];
export const getRuntimeGroupKeyQueryKey = (keyId: string) => [ROOT_LINK_KEY, keyId];

export enum RuntimeGroupQueryErrors {
  GET_RUNTIMES_GROUPS_ERROR = 'GET_RUNTIMES_GROUPS_ERROR',
  GET_RUNTIME_GROUP_ERROR = 'GET_RUNTIME_GROUP_ERROR',
  EDIT_RUNTIME_GROUP_ERROR = 'EDIT_RUNTIME_GROUP_ERROR',
  DELETE_RUNTIME_GROUP_ERROR = 'DELETE_RUNTIME_GROUP_ERROR',
  ADD_RUNTIME_TO_GROUP_ERROR = 'ADD_RUNTIME_TO_GROUP_ERROR',
  REMOVE_RUNTIME_FROM_GROUP_ERROR = 'REMOVE_RUNTIME_FROM_GROUP_ERROR',
  GET_RUNTIMES_GROUP_KEY_ERROR = 'GET_RUNTIMES_GROUP_KEY_ERROR',
  GET_RUNTIMES_GROUP_KEYS_ERROR = 'GET_RUNTIMES_GROUP_KEYS_ERROR',
  ADD_RUNTIMES_GROUP_KEY_ERROR = 'ADD_RUNTIMES_GROUP_KEY_ERROR',
  UPDATE_RUNTIMES_GROUP_KEY_ERROR = 'UPDATE_RUNTIMES_GROUP_KEY_ERROR',
  DELETE_RUNTIMES_GROUP_KEY_ERROR = 'DELETE_RUNTIMES_GROUP_KEY_ERROR',
}

export const useWorkerGroupsQuery = (
  workspaceId: string,
  options?: UseApiQueryOptions<typeof runtimeService.listEnvironmentGroups>,
) => {
  const dispatch = useDispatch();

  return useQuery(
    getRuntimeGroupRootQueryKey(workspaceId),
    async () =>
      validateApiResponse(await runtimeService.listEnvironmentGroups({ workspaceId })).data ?? [],
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimeGroupQueryErrors.GET_RUNTIMES_GROUPS_ERROR }
          ),
        );
      },
      refetchOnMount: true,
      ...options,
    },
  );
};

export const useWorkerGroupQuery = (
  groupId: string,
  options?: UseApiQueryOptions<typeof runtimeService.getEnvironmentGroupById>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getRuntimeGroupQueryKey(groupId),
    async () =>
      validateApiResponse(await runtimeService.getEnvironmentGroupById({ workspaceId, groupId }))
        .data,
    {
      onError: (error) => {
        dispatch({
          ...formatErrorPayload(
            error,
            // { type: RuntimeGroupQueryErrors.GET_RUNTIME_GROUP_ERROR }
          ),
          groupId,
        });
      },
      ...options,
    },
  );
};

export const useCreateWorkerGroupMutation = (
  options?: UseApiMutationOptions<typeof runtimeService.createEnvironmentGroup>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (data: CreateWorkerGroupInput) => {
      const response = validateApiResponse(
        await runtimeService.createEnvironmentGroup({ workspaceId, name: data.name }),
      );
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimesQueryErrors.CREATE_RUNTIME_LINK_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useDeleteWorkerGroupMutation = (
  options?: UseApiMutationOptions<typeof runtimeService.deleteEnvironmentGroupById>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ environmentGroupId }: { environmentGroupId: string }) =>
      validateApiResponse(
        await runtimeService.deleteEnvironmentGroupById({ workspaceId, id: environmentGroupId }),
      ).data,
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimeGroupQueryErrors.DELETE_RUNTIME_GROUP_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useBulkDeleteWorkerGroupMutation = (
  options?: UseApiMutationOptions<typeof runtimeService.deleteEnvironmentGroupById>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (environmentGroupIds: string[]) => {
      const responses: Awaited<ReturnType<typeof runtimeService.deleteEnvironmentGroupById>>[] = [];
      await asyncForLoop(environmentGroupIds, async (groupId) => {
        responses.push(
          await runtimeService.deleteEnvironmentGroupById({ workspaceId, id: groupId }),
        );
      });

      return responses.map((response) => validateApiResponse(response).data);
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      onSuccess: () => queryClient.invalidateQueries(getRuntimeGroupRootQueryKey(workspaceId)),
      ...options,
    },
  );
};

export const useEditWorkerGroupMutation = <
  T extends typeof runtimeService.editEnvironmentGroupById,
>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({
      workerGroupId,
      workerGroup,
    }: {
      workerGroupId: string;
      workerGroup: ApiQueryFnData<T>;
    }) =>
      validateApiResponse(
        await runtimeService.editEnvironmentGroupById({
          workspaceId,
          groupId: workerGroupId,
          group: workerGroup,
        }),
      ).data,
    {
      onSuccess: (_, { workerGroupId, workerGroup }) => {
        queryClient.setQueryData(getRuntimeGroupQueryKey(workerGroupId), workerGroup);
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimeGroupQueryErrors.EDIT_RUNTIME_GROUP_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useAddWorkerToGroup = (
  options?: UseApiMutationOptions<typeof runtimeService.addEnvironmentToGroup>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ workerId, workerGroupId }: { workerId: string; workerGroupId: string }) =>
      validateApiResponse(
        await runtimeService.addEnvironmentToGroup({
          workspaceId,
          groupId: workerGroupId,
          runtimeId: workerId,
        }),
      ).data,
    {
      onSuccess: (_, { workerGroupId }) => {
        queryClient.invalidateQueries(getRuntimeGroupQueryKey(workerGroupId));
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimeGroupQueryErrors.ADD_RUNTIME_TO_GROUP_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useRemoveWorkerFromGroup = (
  options?: UseApiMutationOptions<typeof runtimeService.removeEnvironmentFromGroup>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ workerId, workerGroupId }: { workerId: string; workerGroupId: string }) =>
      validateApiResponse(
        await runtimeService.removeEnvironmentFromGroup({
          workspaceId,
          groupId: workerGroupId,
          runtimeId: workerId,
        }),
      ).data,
    {
      onSuccess: (_, { workerGroupId }) => {
        queryClient.invalidateQueries(getRuntimeGroupQueryKey(workerGroupId));
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimeGroupQueryErrors.REMOVE_RUNTIME_FROM_GROUP_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useWorkerGroupLinkKeysQuery = (
  workerGroupId?: string,
  options?: UseApiQueryOptions<typeof runtimeService.listEnvironmentGroupLinkKeys>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getRuntimeGroupKeyRootQueryKey(),
    async () =>
      validateApiResponse(
        await runtimeService.listEnvironmentGroupLinkKeys({
          workspaceId,
          groupId: workerGroupId || '',
        }),
      ).data ?? [],
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimeGroupQueryErrors.GET_RUNTIMES_GROUP_KEYS_ERROR }
          ),
        );
      },
      ...options,
      enabled: !!workerGroupId,
    },
  );
};

export const useWorkerGroupLinkKeyQuery = (
  keyId: string,
  options?: UseApiQueryOptions<typeof runtimeService.getEnvironmentGroupLinkKey>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    getRuntimeGroupKeyQueryKey(keyId),
    async () =>
      validateApiResponse(await runtimeService.getEnvironmentGroupLinkKey({ workspaceId, keyId }))
        .data,
    {
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimeGroupQueryErrors.GET_RUNTIMES_GROUP_KEY_ERROR }
          ),
        );
      },
      enabled: !!keyId,
      ...options,
    },
  );
};

export const useAddWorkerGroupLinkKeyQuery = <
  T extends typeof runtimeService.addEnvironmentGroupLinkKey,
>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({
      environmentGroupId,
      linkKey,
      autoremoveMinutes,
    }: {
      environmentGroupId: string;
      linkKey: Pick<ApiQueryFnData<T>, 'name' | 'expiry_ts'>;
      autoremoveMinutes: number | null;
    }) =>
      validateApiResponse(
        await runtimeService.addEnvironmentGroupLinkKey({
          workspaceId,
          params: {
            environment_group_id: environmentGroupId,
            name: linkKey.name,
            expiry_ts: linkKey.expiry_ts,
            autoremove_minutes: autoremoveMinutes,
          },
        }),
      ).data,
    {
      onSuccess() {
        queryClient.invalidateQueries(getRuntimeGroupKeyRootQueryKey());
      },
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimeGroupQueryErrors.ADD_RUNTIMES_GROUP_KEY_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useUpdateWorkerGroupLinkKeyQuery = <
  T extends typeof runtimeService.addEnvironmentGroupLinkKey,
>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (linkKey: Pick<ApiQueryFnData<T>, 'id' | 'name' | 'expiry_ts' | 'autoremove_minutes'>) =>
      validateApiResponse(
        await runtimeService.updateEnvironmentGroupLinkKey({ workspaceId, params: linkKey }),
      ).data,
    {
      onSuccess() {
        queryClient.invalidateQueries(getRuntimeGroupKeyRootQueryKey());
      },
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimeGroupQueryErrors.UPDATE_RUNTIMES_GROUP_KEY_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};

export const useDeleteWorkerGroupLinkKeyQuery = (
  options?: UseApiMutationOptions<typeof runtimeService.deleteEnvironmentGroupLinkKey>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (keyId: string) =>
      validateApiResponse(
        await runtimeService.deleteEnvironmentGroupLinkKey({ workspaceId, keyId }),
      ).data,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getRuntimeGroupKeyRootQueryKey());
      },
      onError: (error) => {
        dispatch(
          formatErrorPayload(
            error,
            // { type: RuntimeGroupQueryErrors.DELETE_RUNTIMES_GROUP_KEY_ERROR }
          ),
        );
      },
      ...options,
    },
  );
};
